/**
 * Premium Shipping Protection Auto-Add System
 * Automatically manages shipping protection based on cart value
 */

class ShippingProtection {
  constructor() {
    this.productId = 9502007328989;
    this.protectionEnabled = this.getProtectionState();
    this.isProcessing = false;
    this.lastRequestTime = 0;
    this.REQUEST_COOLDOWN = 2000; // Reduced from 5000ms to 2000ms
    this.hasFixedQuantity = false; // Flag to prevent infinite loops
    this.hasAutoAdded = false; // Flag to prevent auto-add after manual toggle
    this.lastProtectionTier = this.getLastProtectionTier(); // Track last tier to detect changes
    this.cartCache = null; // Cache cart data to avoid redundant API calls
    this.cacheTimeout = 2000; // Cache valid for 2 seconds
    this.variantIds = [
      47944910405853, 47944910438621, 47944910471389, 47944910504157, 47944910536925, 
    47944910569693, 47944910602461, 47944910635229, 47944910667997, 47944910700765,
    47944910733533, 47944910766301, 47944910799069, 47944910831837, 47944910864605,
    47944910897373, 47944910930141, 47944910962909, 47944910995677, 47944911028445,
    47944911061213, 47944911093981, 47944911126749, 47944911159517, 47944911192285,
    47944911225053, 47944911257821, 47944911290589, 47944911323357, 47944911356125,
    47944911388893, 47944911421661, 47944911454429, 47944911487197, 47944911519965,
    47944911552733, 47944911585501, 47944911618269, 47944911651037, 47944911683805,
    47944911716573, 47944911749341, 47944911782109, 47944911814877, 47944911847645,
    47944911880413, 47944911913181, 47944911945949, 47944911978717, 47944912011485,
    47944912044253, 47944912077021, 47944912109789, 47944912142557, 47944912175325,
    47944912208093, 47944912240861, 47944912273629, 47944912306397, 47944912339165,
    47944912371933, 47944912404701, 47944912437469, 47944912470237, 47944912503005,
    47944912535773, 47944912568541, 47944912601309, 47944912634077, 47944912666845,
    47944912699613, 47944912732381, 47944912765149, 47944912797917, 47944912830685,
    47944912863453, 47944912896221, 47944912928989, 47944912961757, 47944912994525,
    47944913027293, 47944913060061, 47944913092829, 47944913125597, 47944913158365,
    47944913191133, 47944913223901, 47944913256669, 47944913289437, 47944913322205,
    47944913354973, 47944913387741, 47944913420509, 47944913453277, 47944913486045,
    47944913518813, 47944913551581, 47944913584349, 47944913617117, 47944913649885
    ];

    this.init();
  }

  init() {
    // Only operate on cart page
    if (window.location.pathname !== '/cart') {
      return;
    }

    // Listen for cart updates
    document.addEventListener('DOMContentLoaded', () => {
      this.showProtectionSection();
      this.setupCheckboxHandler();
      this.updateProtectionDisplay();
      // Auto-add protection on page load if enabled - do this immediately
      this.autoAddProtectionOnLoad();
    });
  }

  async getCartData(useCache = true) {
    // Use cached data if available and recent
    if (useCache && this.cartCache && (Date.now() - this.cartCache.timestamp) < this.cacheTimeout) {
      return this.cartCache.data;
    }

    // Fetch fresh cart data
    const response = await fetch('/cart.js');
    const cart = await response.json();
    
    // Cache the result
    this.cartCache = {
      data: cart,
      timestamp: Date.now()
    };

    return cart;
  }

  async getCartSubtotal(useCache = true) {
    // Get cart subtotal excluding shipping protection
    const cart = await this.getCartData(useCache);
    let subtotal = 0;
    cart.items.forEach(item => {
      if (item.product_id !== this.productId) {
        subtotal += item.line_price;
      }
    });
    return subtotal;
  }

  async getCurrentProtectionItem(useCache = true) {
    const cart = await this.getCartData(useCache);
    
    const protectionItems = cart.items.filter(item => item.product_id === this.productId);
    
    if (protectionItems.length > 1) {
      this.removeDuplicateProtection(protectionItems);
      return protectionItems[0]; // Return the first one
    }
    
    const protectionItem = protectionItems[0] || null;
    return protectionItem;
  }

  calculateRequiredTier(subtotal) {

  const dollars = subtotal / 100;

  // $0 - $1000
  if (dollars <= 1000) {
    return Math.ceil(dollars / 100);
  }

  // $1001 - $15000
  if (dollars <= 15000) {
    return Math.ceil(dollars / 1000) + 9;
  }

  // $15001+
  if (dollars <= 50000) {
    return Math.ceil(dollars / 1000) + 9;
  }

  // Hard cap
  return 100;
}

  getVariantIdForTier(tier) {
    // Tiers are 1-indexed, array is 0-indexed
    return this.variantIds[tier - 1];
  }

  getProtectionState() {
    // Default to enabled (true) unless explicitly disabled
    return localStorage.getItem('shipping_protection_enabled') !== 'false';
  }

  setProtectionState(enabled) {
    this.protectionEnabled = enabled;
    localStorage.setItem('shipping_protection_enabled', enabled.toString());
  }

  getLastProtectionTier() {
    return parseInt(localStorage.getItem('last_protection_tier')) || 0;
  }

  setLastProtectionTier(tier) {
    this.lastProtectionTier = tier;
    localStorage.setItem('last_protection_tier', tier.toString());
  }

  async updateProtection() {
    // Rate limiting check
    const now = Date.now();
    if (this.isProcessing || (now - this.lastRequestTime) < this.REQUEST_COOLDOWN) {
      return;
    }

    this.isProcessing = true;
    this.lastRequestTime = now;

    try {
      if (!this.protectionEnabled) {
        await this.removeProtection();
        return;
      }

      const subtotal = await this.getCartSubtotal();
      const currentProtection = await this.getCurrentProtectionItem();
      const requiredTier = this.calculateRequiredTier(subtotal);
      
      if (subtotal === 0) {
        // Empty cart, remove protection
        if (currentProtection) {
          await this.removeProtection();
        }
        return;
      }

      const requiredVariantId = this.getVariantIdForTier(requiredTier);

      if (!currentProtection) {
        // No protection in cart, add it
        await this.addProtection(requiredVariantId);
      } else if (currentProtection.variant_id !== requiredVariantId) {
        // Wrong tier, update it
        await this.updateProtectionTier(currentProtection, requiredVariantId);
      }
    } finally {
      // Reset processing flag after cooldown period
      setTimeout(() => {
        this.isProcessing = false;
      }, this.REQUEST_COOLDOWN / 2); // Reset flag sooner than full cooldown
    }
  }

  async addProtection(variantId, shouldReload = false) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });
      
      if (response.status === 429) {
        setTimeout(() => this.addProtection(variantId, shouldReload), 3000);
        return;
      }
      
      if (response.ok) {
        // Invalidate cache since cart changed
        this.cartCache = null;
        
        if (shouldReload) {
          setTimeout(() => window.location.reload(), 300);
        } else {
          // Update displays immediately with reduced delay
          setTimeout(() => this.updateProtectionDisplay(), 200);
        }
      }
    } catch (error) {
      // Silent error handling
    }
  }

  async removeProtection() {
    const currentProtection = await this.getCurrentProtectionItem();
    if (currentProtection) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: currentProtection.key,
            quantity: 0
          })
        });
        
        if (response.ok) {
          // Invalidate cache since cart changed
          this.cartCache = null;
          return true;
        } else {
          return false;
        }
      } catch (error) {
        return false;
      }
    } else {
      return true;
    }
  }

  async updateProtectionTier(currentProtection, newVariantId, shouldReload = false) {
    // Remove current protection and add new tier
    await this.removeProtection();
    await this.addProtection(newVariantId, shouldReload);
  }

  setupCheckboxHandler() {
    const checkbox = document.getElementById('shipping-protection-toggle');
    if (checkbox) {
      checkbox.checked = this.protectionEnabled;
      checkbox.addEventListener('change', (e) => {
        this.setProtectionState(e.target.checked);
        
        if (e.target.checked) {
          this.manualUpdateProtection().then(() => {
            setTimeout(() => window.location.reload(), 300);
          });
        } else {
          this.removeProtection().then((success) => {
            if (success) {
              setTimeout(() => window.location.reload(), 300);
            } else {
              e.target.checked = true;
              this.setProtectionState(true);
            }
          });
        }
      });
    }
  }

  observeCartChanges() {
    // Listen for cart form submissions
    const cartForm = document.querySelector('form[action="/cart"]');
    if (cartForm) {
      cartForm.addEventListener('submit', () => {
        // Delay to allow cart update to process
        setTimeout(() => this.updateProtection(), 300);
      });
    }
  }



  async removeDuplicateProtection(protectionItems) {
    for (let i = 1; i < protectionItems.length; i++) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: protectionItems[i].key,
            quantity: 0
          })
        });
      } catch (error) {
        // Silent error handling
      }
    }
  }

  async fixProtectionQuantity(protectionItem) {
    // Fix protection quantity to always be 1
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: protectionItem.key,
          quantity: 1
        })
      });
      
      if (response.ok) {
        console.log('Protection quantity fixed successfully');
        // Refresh to show the updated quantity
        setTimeout(() => window.location.reload(), 500);
      } else {
        console.error('Failed to fix protection quantity:', response.status);
      }
    } catch (error) {
      console.error('Error fixing protection quantity:', error);
    }
  }

  showProtectionSection() {
    const section = document.getElementById('shipping-protection-section');
    if (section) {
      section.style.display = 'block';
    }
  }


  async updateProtectionDisplay() {
  const subtotal = await this.getCartSubtotal(); // in cents
  const tier = this.calculateRequiredTier(subtotal);

  const coveredValueDollars = tier * 1000; // <-- key change: use tier coverage blocks

  const tierPrices = {
  1: 5,
  2: 10,
  3: 10,
  4: 12,
  5: 15,
  6: 18,
  7: 21,
  8: 24,
  9: 27,
  10: 30,
  11: 60,
  12: 90,
  13: 120,
  14: 150,
  15: 180,
  16: 210,
  17: 240,
  18: 270,
  19: 300,
  20: 300,
  21: 300,
  22: 300,
  23: 300,
  24: 300,
  25: 320,
  26: 340,
  27: 360,
  28: 380,
  29: 400,
  30: 420,
  31: 440,
  32: 460,
  33: 480,
  34: 500,
  35: 520,
  36: 540,
  37: 560,
  38: 580,
  39: 600,
  40: 620,
  41: 640,
  42: 660,
  43: 680,
  44: 700,
  45: 720,
  46: 740,
  47: 760,
  48: 780,
  49: 800,
  50: 820
};

const price = tierPrices[tier] || 980;

  // Optional: round to whole dollars for display
  const displayPrice = Math.round(price);

  const priceDisplay = document.getElementById('protection-price-display');
  if (priceDisplay) {
    priceDisplay.textContent = `Cost: $${displayPrice}.00`;
  }

const subtotalNotes = document.querySelectorAll('.psp-subtotal-note');

subtotalNotes.forEach(note => {

  if (this.protectionEnabled) {
    note.style.display = 'block';
  } else {
    note.style.display = 'none';
  }

});

await this.updateDisplayedCartTotal();


  await this.updateFallbackProtectionDisplay();
}


  async updateFallbackProtectionDisplay() {
    const subtotal = await this.getCartSubtotal();
    const currentProtection = await this.getCurrentProtectionItem();
    const fallbackElement = document.getElementById('protection-price-fallback');
    const priceAmountElement = document.getElementById('protection-price-amount');
    
    if (fallbackElement && priceAmountElement) {
      if (this.protectionEnabled && currentProtection && currentProtection.line_price > 0 && subtotal > 0) {
        // Show fallback with current protection price
        const priceFormatted = this.formatMoney(currentProtection.line_price);
        priceAmountElement.textContent = priceFormatted + ' CAD';
        fallbackElement.style.display = 'flex';
      } else {
        // Hide fallback
        fallbackElement.style.display = 'none';
      }
    }
  }

  formatMoney(cents) {
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  }



async updateDisplayedCartTotal() {

  const cart = await this.getCartData(false);

  let productTotal = 0;
  let existingPspTotal = 0;

  cart.items.forEach(item => {

    if (item.product_id === this.productId) {

      existingPspTotal += item.final_line_price || item.line_price || 0;

    } else {

      productTotal += item.final_line_price || item.line_price || 0;

    }

  });

  const subtotal = productTotal;
  const tier = this.calculateRequiredTier(subtotal);

  const tierPrices = {
    1: 500,
    2: 1000,
    3: 1000,
    4: 1200,
    5: 1500,
    6: 1800,
    7: 2100,
    8: 2400,
    9: 2700,
    10: 3000,
    11: 6000,
    12: 9000,
    13: 12000,
    14: 15000,
    15: 18000,
    16: 21000,
    17: 24000,
    18: 27000,
    19: 30000,
    20: 30000,
    21: 30000,
    22: 30000,
    23: 30000,
    24: 30000,
    25: 32000,
    26: 34000,
    27: 36000,
    28: 38000,
    29: 40000,
    30: 42000,
    31: 44000,
    32: 46000,
    33: 48000,
    34: 50000,
    35: 52000,
    36: 54000,
    37: 56000,
    38: 58000,
    39: 60000,
    40: 62000,
    41: 64000,
    42: 66000,
    43: 68000,
    44: 70000,
    45: 72000,
    46: 74000,
    47: 76000,
    48: 78000,
    49: 80000,
    50: 82000
  };

  let displayPspTotal = 0;

  if (this.protectionEnabled && subtotal > 0) {

    if (existingPspTotal > 0) {

      displayPspTotal = existingPspTotal;

    } else {

      displayPspTotal = tierPrices[tier] || 98000;

    }

  }

  const displayTotal = productTotal + displayPspTotal;

  document.querySelectorAll('[data-cart-total]').forEach(el => {
    el.textContent = this.formatMoney(displayTotal);
  });

}




  async autoAddProtectionOnLoad() {
    if (!this.protectionEnabled) {
      this.setLastProtectionTier(0);
      return;
    }

    const subtotal = await this.getCartSubtotal();
    const currentProtection = await this.getCurrentProtectionItem();
    const requiredTier = this.calculateRequiredTier(subtotal);
    
    if (subtotal === 0) {
      this.setLastProtectionTier(0);
      return;
    }

    const tierChanged = this.lastProtectionTier !== requiredTier;

    if (currentProtection) {
      const requiredVariantId = this.getVariantIdForTier(requiredTier);
      
      if (currentProtection.quantity !== 1 && !this.hasFixedQuantity) {
        this.hasFixedQuantity = true;
        await this.fixProtectionQuantity(currentProtection);
        return;
      }
      
      if (currentProtection.variant_id === requiredVariantId) {
        this.setLastProtectionTier(requiredTier);
        return;
      } else {
        this.setLastProtectionTier(requiredTier);
        await this.updateProtectionTier(currentProtection, requiredVariantId, true);
        return;
      }
    }

    const requiredVariantId = this.getVariantIdForTier(requiredTier);
    const shouldReload = tierChanged && this.lastProtectionTier !== 0;
    this.setLastProtectionTier(requiredTier);
    this.hasAutoAdded = true;
    await this.addProtection(requiredVariantId, shouldReload);
  }

  async manualUpdateProtection() {
    try {
      const subtotal = await this.getCartSubtotal();
      const currentProtection = await this.getCurrentProtectionItem();
      const requiredTier = this.calculateRequiredTier(subtotal);
      
      if (subtotal === 0) return;

      const requiredVariantId = this.getVariantIdForTier(requiredTier);

      if (!currentProtection) {
        await this.addProtection(requiredVariantId);
      } else if (currentProtection.variant_id !== requiredVariantId) {
        await this.updateProtectionTier(currentProtection, requiredVariantId);
      }
    } catch (error) {
      // Silent error handling
    }
  }
}

// Initialize shipping protection
window.shippingProtection = new ShippingProtection();